import { useMemo, useState } from 'react';
import {
    MaterialReactTable,
    MRT_ColumnDef,
    type MRT_Row,
    MRT_RowData,
    type MRT_TableOptions,
    useMaterialReactTable,
} from 'material-react-table';
import {
    Box,
    Button,
    Card,
    Grid,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { generateUUID } from '@/shared';
import { getAllIpv4 } from '@/shared/actions/erp';
import { useUiStore } from '@/store/ui';
import { toast } from 'react-toastify';
import { EstadoIpv4, Ipv4 } from '@/shared/interfaces';


interface SampleDataEditProps<MigracionRadius extends MRT_RowData>{
    minHeight?: string;
    maxHeight?: string;
    data: MigracionRadius[];
    setRowsData: React.Dispatch<React.SetStateAction<MigracionRadius[]>>;
    editedItems: Record<string, MigracionRadius>;
    setEditedItems: React.Dispatch<React.SetStateAction<Record<string, MigracionRadius>>>;
    key?: number;
    setTableKey?: React.Dispatch<React.SetStateAction<number>>;
    paramsIpv4?: Partial<Ipv4>;
    optionsValueRadreply: {value: string, label: string}[];
}

export const TableDataEditMigracionesRadius = <MigracionRadius extends MRT_RowData>({
    minHeight,
    maxHeight,
    data,
    setRowsData,
    editedItems,
    setEditedItems,
    key,
    setTableKey,
    paramsIpv4,
    optionsValueRadreply
}:SampleDataEditProps<MigracionRadius>) => {
    const [validationErrors, setValidationErrors] = useState<
        Record<string, string | undefined>
    >({});
    const setIsGlobalLoading = useUiStore((s) => s.setIsGlobalLoading);


    const cargarIpv4Items = async () => {
        try{
            if(!paramsIpv4 || !paramsIpv4.grupo_ipv4) {
                return toast.error('Debe seleccionar un BRAS destino');
            }
            setIsGlobalLoading(true);
            const ipv4Data = await getAllIpv4({
                ...paramsIpv4,
                estado_ip: EstadoIpv4.DISPONIBLE,
                estado:true,
                page_size: data.length,
                random: true,
                
            })
            if(ipv4Data?.data?.data?.length < data.length) {
                setIsGlobalLoading(false);
                return toast.error(`No hay suficientes IPs disponibles. Faltan ${data.length - ipv4Data?.data?.data?.length} IPs.`);
            }
            const updatedData = data.map((item, index) => ({
                ...item,
                ip: ipv4Data?.data?.data?.[index]?.ip || ''
            }));

            setRowsData(updatedData);

            // Sincronizar editedItems también por si hay cambios pendientes
            const updatedEdited = Object.fromEntries(
                Object.entries(editedItems).map(([key, item]) => {
                    const dataIndex = data.findIndex((d) => d.id === item.id);
                    return [
                        key,
                        { ...item, ip: ipv4Data?.data?.data?.[dataIndex]?.ip || '' }
                    ];
                })
            );
            setEditedItems(updatedEdited);
            setTableKey?.(prev => prev + 1);
            setIsGlobalLoading(false)

        }catch(error){
            console.error(error);
            toast.error('Error al cargar las IPs');
        }finally{
            setIsGlobalLoading(false);
        }
    };


    


    const columns = useMemo<MRT_ColumnDef<MigracionRadius>[]>(
        () => [
            {
                accessorKey: 'pppoe',
                header: 'Usuario',
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.[cell.id],
                    helperText: validationErrors?.[cell.id],
                    //store edited item in state to be saved later
                    onBlur: (event) => {
                        const validationError = !validateRequired(event.currentTarget.value)
                            ? 'Campo Obligatorio'
                            : undefined;
                        setValidationErrors({ ...validationErrors, [cell.id]: validationError });
                        const previousValue = editedItems[row.id]?.pppoe ?? row.original.pppoe;
                        if (previousValue !== event.currentTarget.value) {
                            setEditedItems({
                                ...editedItems,
                                [row.id]: { ...row.original, ...editedItems[row.id], pppoe: event.currentTarget.value },
                            });
                        }
                    },
                }),
            },
            {
                accessorKey: 'password',
                header: 'Contraseña',
                size:300,
                muiEditTextFieldProps: ({ cell, row }) => ({
                    placeholder:"Por defecto, valor del usuario.",
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.[cell.id],
                    helperText: validationErrors?.[cell.id],
                    //store edited item in state to be saved later
                    onBlur: (event) => {
                        // const validationError = !validateRequired(event.currentTarget.value)
                        //     ? 'Campo Obligatorio'
                        //     : undefined;
                        // setValidationErrors({ ...validationErrors, [cell.id]: validationError });
                        const previousValue = editedItems[row.id]?.password ?? row.original.password;
                        if (previousValue !== event.currentTarget.value) {
                            setEditedItems({
                                ...editedItems,
                                [row.id]: { ...row.original, ...editedItems[row.id], password: event.currentTarget.value },
                            });
                        }
                    },
                }),
            },
            {
                accessorKey: 'ip',
                header: 'IPv4',
                muiEditTextFieldProps: ({ cell, row }) => ({
                    type: 'text',
                    required: true,
                    error: !!validationErrors?.[cell.id],
                    helperText: validationErrors?.[cell.id],
                    onBlur: (event) => {
                        const validationError = !!!(event.currentTarget.value)
                            ? undefined
                            : !validateIpv4(event.currentTarget.value)
                                ? 'IPv4 inválido'
                                : undefined;
                        setValidationErrors({ ...validationErrors, [cell.id]: validationError });
                        const previousValue = editedItems[row.id]?.ip ?? row.original.ip;
                        if (previousValue !== event.currentTarget.value) {
                            setEditedItems({
                                ...editedItems,
                                [row.id]: { ...row.original, ...editedItems[row.id], ip: event.currentTarget.value },
                            });
                        }
                    },
                }),
            },
            {
                accessorKey: 'value_radreply',
                header: 'Valor',
                editVariant: 'select',
                editSelectOptions: optionsValueRadreply,
                muiEditTextFieldProps: ({ cell, row }) => ({  // ← agrega cell
                    select: true,
                    error: !!validationErrors?.[cell.id],   // ← usa cell.id
                    helperText: validationErrors?.[cell.id], // ← usa cell.id
                    onChange: (event) => {
                        const previousValue = editedItems[row.id]?.value_radreply ?? row.original.value_radreply;
                        if (previousValue != event.target.value) {
                            setEditedItems({
                                ...editedItems,
                                [row.id]: { ...row.original, ...editedItems[row.id], value_radreply: event.target.value },
                            });
                        }
                    },
                }),
            },
        ],
        [editedItems, validationErrors],
    );

    //CREATE action
    const handleCreateRow: MRT_TableOptions<MigracionRadius>['onCreatingRowSave'] = async ({
        values,
        table,
    }) => {
        // Solo validar campos reales, no metadatos de MRT
        const fieldsToValidate = ['pppoe', 'value_radreply'];
        const hasErrors = fieldsToValidate.some((key) => !validateRequired(values[key]));

        if (hasErrors) {
            // Usar el mismo formato de cell.id que usa MRT: `mrt-row-create_fieldName`
            const errors = Object.fromEntries(
                fieldsToValidate
                    .filter((key) => !validateRequired(values[key]))
                    .map((key) => [`mrt-row-create_${key}`, 'Campo Obligatorio'])
            );
            setValidationErrors(errors);
            return;
        }
        (values as any).id = generateUUID()
        setValidationErrors({});
        setRowsData([...data, values as MigracionRadius]);
        table.setCreatingRow(null);
    };

    //UPDATE action
    const handleSaveRows = async () => {
        if (Object.values(validationErrors).some((error) => !!error)) return;

        // Excluir la fila temporal de creación
        const validEditedItems = Object.fromEntries(
            Object.entries(editedItems).filter(([key]) => key !== 'mrt-row-create')
        ) as Record<string, MigracionRadius>;

        // Fusionar: mantener todos los rows originales, reemplazar solo los editados
        const updatedRows = data.map((row) => validEditedItems[row.id] ?? row);

        setRowsData(updatedRows);
        setEditedItems({});
    };

    //DELETE action
    const openDeleteConfirmModal = (row: MRT_Row<MigracionRadius>) => {
        if (window.confirm('¿Seguro que deseas eliminar este item?')) {
            setRowsData(data.filter((item) => item.id !== row.original.id));
        }
    };

    const table = useMaterialReactTable({
        columns,
        data: data,
        createDisplayMode: 'row', // ('modal', and 'custom' are also available)
        editDisplayMode: 'table', // ('modal', 'row', 'cell', and 'custom' are also
        enableEditing: true,
        enableRowActions: true,
        positionActionsColumn: 'first',
        localization: {
            noRecordsToDisplay: 'Aún no se han agregado registros',
        },
        displayColumnDefOptions: {
            'mrt-row-actions': {
                header: 'Acciones',
            },
        },
        getRowId: (row) => row.id,
        muiTableContainerProps: {
            sx: {
                minHeight: minHeight || '150px',
                maxHeight: maxHeight || '300px',
            },
        },
        onCreatingRowCancel: () => setValidationErrors({}),
        onCreatingRowSave: handleCreateRow,
        renderRowActions: ({ row }) => (
            <Box sx={{ display: 'flex', gap: '1rem' }}>
                <Tooltip title="Delete">
                    <IconButton color="error" onClick={() => openDeleteConfirmModal(row)}>
                        <DeleteIcon />
                    </IconButton>
                </Tooltip>
            </Box>
        ),
        renderBottomToolbarCustomActions: () => (
            <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                <Button
                    color="success"
                    variant="contained"
                    onClick={handleSaveRows}
                    disabled={
                        Object.keys(editedItems).length === 0 
                        || Object.values(validationErrors).some((error) => !!error)
                    }
                >
                    Guardar
                </Button>
                {Object.values(validationErrors).some((error) => !!error) && (
                    <Typography color="error">Se presentó un error al guardar los cambios.</Typography>
                )}
            </Box>
        ),
        renderTopToolbarCustomActions: ({ table }) => (
            <Grid container gap={1} spacing={1} pt={2}>
                <Button
                    variant="contained"
                    color="success"
                    disabled={!!table.getState().creatingRow}
                    onClick={() => {
                        table.setCreatingRow(true); //simplest way to open the create row modal with no default values
                        //or you can pass in a row object to set default values with the `createRow` helper function
                        // table.setCreatingRow(
                        //   createRow(table, {
                        //     //optionally pass in default values for the new row, useful for nested data or other complex scenarios
                        //   }),
                        // );
                    }}
                >
                    Añadir
                </Button>
                <Button
                    variant="contained"
                    color="warning"
                    disabled={data.length === 0}
                    onClick={() => {
                        cargarIpv4Items();
                    }}
                >
                    Cargar IPs
                </Button>
            </Grid>
        ),
        state: {
            // isLoading: isLoadingItems,
            // isSaving: isUpdatingItems || isDeletingUser,
            // showAlertBanner: isLoadingItemsError,
            // showProgressBars: isFetchingItems,
        },
    });

    return (
        <Card key={key}>
            <Box
                sx={{
                    width: '100%',
                }}
            >
                <MaterialReactTable table={table} />
            </Box>
        </Card>
    );
};


const validateRequired = (value: string) => !!value.length;
const validateIpv4 = (value: string) => {
    const ipv4Regex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-4]|2[0-4][0-9]|1?[0-9]{1,2})$/;
    return ipv4Regex.test(value);
};